import { Box, Stack, InputAdornment } from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { useCustom, useTranslate } from "@refinedev/core";
import { CreateOrEdit } from "@/components/editcreate";
import { TextFieldElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { CouponSchema } from "@/utils/zod/couponValidation";
import RHFDateRangePicker from "@/components/form/RHFDateRangePicker";
import dayjs from "dayjs";
import { z } from "zod";
import RHFAutoCompleteString from "@/components/form/RHFAutocompleteString";

type CouponFormData = z.infer<typeof CouponSchema>;

export default function CreateEditCoupon({ actionType }: { actionType: 'create' | 'edit' }) {
    const t = useTranslate();

    const formValues = useForm<CouponFormData>({
        refineCoreProps: {
            resource: "cart-coupons",
            redirect: "list",
            // Add any edit-specific options here if needed
        },
        resolver: zodResolver(CouponSchema),
        defaultValues: actionType === "create" ? {
            category: "",
            status: "active",
            discount_type: "percentage",
            start_date: dayjs().add(1, 'day').toDate(),
            expiry_date: dayjs().add(7, 'day').toDate(),
            description: "",
            discount_value: 0,
            discount_applies_above: 0,
        } : undefined
    });

    const { data: companyCategories } = useCustom<string[]>({
        url: "/companies/company-categories",
        method: "get"
    });

    const onSubmit = formValues.handleSubmit((data) => {
        formValues.refineCore.onFinish({
            ...data,
            code: data.code.toUpperCase(),
        })
    })

    return (
        <CreateOrEdit actionType={actionType} formValues={formValues} handleSubmit={onSubmit}>
            <Box sx={{ p: 3 }}>
                <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Stack direction="row" gap={2}>
                        <RHFAutoCompleteString
                            name="category"
                            label={t("company.fields.company-category")}
                            options={companyCategories?.data || []}
                            size="small"
                            sx={{ flex: 0.5 }}
                            onBlur={(e) => {
                                formValues.setValue("description", `Discount of ${formValues.getValues("discount_value")}% applies above ₹${formValues.getValues("discount_applies_above")} for ${e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1)} company category`);
                            }}
                            required={true}
                        />

                        <TextFieldElement
                            name="code"
                            label={t("coupons.fields.code")}
                            autoFocus
                            size="small"
                            sx={{ flex: 0.5 }}
                            slotProps={{
                                htmlInput: {
                                    style: {
                                        textTransform: "uppercase",
                                    }
                                },
                                inputLabel: {
                                    sx: {
                                      '& .MuiFormLabel-asterisk': {
                                        color: 'error.main',
                                        fontSize: '24px',
                                      },
                                    },
                                  }
                            }}
                            required={true}
                        />
                        <TextFieldElement
                            name="discount_value"
                            label={t("coupons.fields.percentageDiscount")}
                            type="number"
                            fullWidth
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }
                            }}
                            sx={{ flex: 0.5 }}
                            size="small"
                            onBlur={(e) => {
                                formValues.setValue("description", `Discount of ${e.target.value}% applies above ₹${formValues.getValues("discount_applies_above")} for ${formValues.getValues("category").charAt(0).toUpperCase() + formValues.getValues("category").slice(1)} company category`);
                            }}
                        />

                        <TextFieldElement
                            name="discount_applies_above"
                            label={t("coupons.fields.discountAppliesAbove")}
                            type="number"
                            fullWidth
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                }
                            }}
                            sx={{ flex: 0.5 }}
                            size="small"
                            onBlur={(e) => {
                                formValues.setValue("description", `Discount of ${formValues.getValues("discount_value")}% applies above ₹${e.target.value} for ${formValues.getValues("category").charAt(0).toUpperCase() + formValues.getValues("category").slice(1)} company category`);
                            }}
                        />
                    </Stack>


                    <TextFieldElement
                            name="description"
                            label={t("coupons.fields.description")}
                            multiline
                            fullWidth
                            size="small"
                            sx={{ flex: 1.5 }}
                        />
                    <Stack direction="row" gap={2}>
                        <RHFDateRangePicker sx={{ flex: 1 }} size="small" />
                        <RHFAutoCompleteString
                            name="status"
                            label={t("coupons.fields.status")}
                            options={["active", "inactive"]}
                            sx={{ flex: 1 }}
                            fullWidth
                            size="small"
                            required={true}
                        />
                    </Stack>

                </Box>
            </Box>
        </CreateOrEdit>
    );
}